import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DonationOrgInfo,
  ThankYouComponent,
} from './thank-you/thank-you.component';
import { ModalNavigationService } from '../../services/modal-navigation.service';
import { Store } from '@ngrx/store';
import { combineLatest, map, shareReplay, take } from 'rxjs';
import {
  DonationFlowUiActions,
  hideFeeReview,
  selectCheckoutSummaryVM,
  selectDonationMetadata,
  selectDonationType,
  selectFirstPaymentDate,
  selectPriceDraft,
  selectRestartCustomerRequest,
  selectSelectedCampaign,
} from '../../state/donation-flow';
import {
  DonationSummaryVM,
  DonationSummaryCardComponent,
} from './sumary-card/donation-summary-card.component';
import { RecurringPaymentActions, OneTimePaymentActions } from '../../state/payment/actions';
import { DonationType, type PriceDraft } from '../../types/donation-flow.models';

type MonthlyJoinFormValue = {
  firstPaymentDate: string | null;
  unitAmountMajor: number | null;
};

@Component({
    selector: 'don-thank-you-step',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ThankYouComponent,
        DonationSummaryCardComponent,
    ],
    templateUrl: './thank-you-step.component.html',
    styleUrls: ['./thank-you-step.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThankYouStepComponent implements OnInit {
  private location = inject(Location);
  private store = inject(Store);
  private modalNavigationService = inject(ModalNavigationService);

  title = 'CAMPAIGN.ThankYouMessage';
  communityTitle = 'CAMPAIGN.BecomePartOfCommunity';
  subtitle = 'CAMPAIGN.MonthlyDonorsMakeADifference';
  descriptionLines = [
    'CAMPAIGN.JoinTheCommunity',
    'CAMPAIGN.YoureNotJustGiving',
  ];

  readonly monthlyJoinForm = new FormGroup({
    firstPaymentDate: new FormControl<string | null>(null, {
      validators: [Validators.required],
    }),
    unitAmountMajor: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(1)],
    }),
  });

  summary$ = combineLatest({
    summary: this.store.select(selectCheckoutSummaryVM),
    meta: this.store.select(selectDonationMetadata),
    campaign: this.store.select(selectSelectedCampaign),
  }).pipe(
    map(
      ({ summary, meta, campaign }): DonationSummaryVM => {
        const fees = summary.donationFees ?? 0;
        return {
          donationType: summary.donationType!,
          currency: summary.currency || 'USD',
          amount: summary.donationAmount,
          fees,
          feesCovered: summary.feesCovered,
          total: summary.feesCovered
            ? summary.donationAmount + fees
            : summary.donationAmount,
          campaignName: campaign?.nameEn ?? campaign?.nameEn ?? null,
          donorName: (meta as any)?.customerName ?? null,
          donorEmail: (meta as any)?.customerEmail ?? null,
        };
      }
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  showMonthlyCta$ = this.summary$.pipe(
    map((summary) => summary.donationType === DonationType.OneTime)
  );

  org: DonationOrgInfo = {
    legalName: 'Crezco Foundation Inc',
    ein: '921100986',
    email: 'info@crezcofoundation.org',
    verified: true,
  };

  ngOnInit(): void {
    combineLatest([
      this.store.select(selectPriceDraft),
      this.store.select(selectFirstPaymentDate),
    ])
      .pipe(take(1))
      .subscribe(([priceDraft, firstPaymentDate]) => {
        this.monthlyJoinForm.patchValue(
          {
            firstPaymentDate: firstPaymentDate ?? null,
            unitAmountMajor: priceDraft?.unitAmount
              ? Number(priceDraft.unitAmount) / 100
              : null,
          },
          { emitEvent: false }
        );
      });
  }

  onClose = () => {
    this.store
      .select(selectDonationType)
      .pipe(take(1))
      .subscribe((donationType) => {
        if (donationType === DonationType.Recurring) {
          this.store.dispatch(RecurringPaymentActions.viewExited());
          this.store.dispatch(
            RecurringPaymentActions.setClientSecret({ clientSecret: null })
          );
          this.store.dispatch(
            RecurringPaymentActions.setActive({ active: false })
          );
        } else {
          this.store.dispatch(OneTimePaymentActions.viewExited());
          this.store.dispatch(
            OneTimePaymentActions.setClientSecret({ clientSecret: null })
          );
          this.store.dispatch(
            OneTimePaymentActions.setActive({ active: false })
          );
        }

        this.store.dispatch(hideFeeReview());
        this.store.dispatch(DonationFlowUiActions.reset());

        this.modalNavigationService.closeModal();
      });
  };

  private toMinorUnits(amountMajor: number | null): string | null {
    if (
      amountMajor === null ||
      amountMajor === undefined ||
      Number.isNaN(amountMajor)
    ) {
      return null;
    }

    return Math.round(amountMajor * 100).toString();
  }

  onJoinMonthly = (): void => {
  if (this.monthlyJoinForm.invalid) {
    this.monthlyJoinForm.markAllAsTouched();
    return;
  }

  const rawValue = this.monthlyJoinForm.getRawValue() as MonthlyJoinFormValue;

  const firstPaymentDate = rawValue.firstPaymentDate ?? null;
  const unitAmountMajor = rawValue.unitAmountMajor ?? null;
  const nextUnitAmountMinor = this.toMinorUnits(unitAmountMajor);

  combineLatest([
    this.store.select(selectRestartCustomerRequest),
    this.store.select(selectPriceDraft),
    this.store.select(selectDonationMetadata),
  ])
    .pipe(take(1))
    .subscribe(([customerRequest, priceDraft, metadata]) => {
      if (!customerRequest || !priceDraft || !metadata) {
        return;
      }

      const nextDraft: PriceDraft = {
        ...priceDraft,
        unitAmount: nextUnitAmountMinor ?? priceDraft.unitAmount,
      };

      const nextMetadata = {
        ...metadata,
        donationType: DonationType.Recurring,
        productId: nextDraft.productId ?? metadata.productId,
        productFeeId: nextDraft.productFeeId ?? metadata.productFeeId,
      };

      this.store.dispatch(DonationFlowUiActions.resetCardBrandState());

      /**
       * Update state first.
       *
       * `changeDonationType` (not the plain `setDonationType`) is required
       * here: it clears the stale, already-consumed one-time `setupIntent`
       * from the store and resets the previous payment branch (see
       * `resetBothBranchesOnDonationTypeChange$`). Without it, the recurring
       * Payment Element could transiently mount using the one-time flow's
       * already-completed SetupIntent client secret, leaving the card field
       * stuck.
       */
      this.store.dispatch(
        DonationFlowUiActions.changeDonationType({
          donationType: DonationType.Recurring,
        })
      );

      this.store.dispatch(
        DonationFlowUiActions.setDonationMetadata({
          metadata: nextMetadata,
        })
      );

      this.store.dispatch(
        DonationFlowUiActions.setDeferFirstCharge({
          isFirstChargeDeferred: true,
        })
      );

      this.store.dispatch(
        DonationFlowUiActions.setFirstPaymentDate({
          firstPaymentDate,
        })
      );

      this.store.dispatch(
        DonationFlowUiActions.setPriceDraft({
          draft: nextDraft,
        })
      );

      this.store.dispatch(
        DonationFlowUiActions.setStep({ step: 2 })
      );

      /**
       * Then preload recurring setup / Stripe Payment Element
       */
      this.store.dispatch(
        DonationFlowUiActions.beginStep2Recurring({
          customer: customerRequest,
        })
      );

      /**
       * Finally navigate
       */
      this.modalNavigationService.navigateToPaymentStep(DonationType.Recurring, 2);
    });
};
}
